import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { SystemService, type PostApiSystemApiKeysData } from "@/lib/api_client";
import { getAuthHeaders } from "@/lib/api";

export type NewApiKey = PostApiSystemApiKeysData["body"];
export type ApiKeyScope = NewApiKey["scope"];

export const useApiKeys = () =>
  useQuery({
    queryKey: ["system", "api-keys"],
    queryFn: async () => {
      const res = await SystemService.getApiSystemApiKeys({ headers: getAuthHeaders() });
      if (!res.data) throw new Error("No data returned");
      return res.data;
    },
  });

export const useApiKeyUsage = (keyId: string | null, page: { limit: number; offset: number }) =>
  useQuery({
    queryKey: ["system", "api-keys", keyId, "usage", page],
    queryFn: async () => {
      const res = await SystemService.getApiSystemApiKeysByIdUsage({
        path: { id: keyId! },
        query: { limit: String(page.limit), offset: String(page.offset) },
        headers: getAuthHeaders(),
      });
      if (!res.data) throw new Error("No data returned");
      return res.data;
    },
    enabled: !!keyId,
  });

/** The response holds the only copy of the key; it is never cached. */
export const useCreateApiKey = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (body: NewApiKey) => {
      const res = await SystemService.postApiSystemApiKeys({
        body,
        headers: getAuthHeaders(),
      });
      if (!res.data) throw new Error((res.error as any)?.error ?? "Kunne ikke lage nøkkel");
      return res.data;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ["system", "api-keys"] }),
  });
};

export const useRevokeApiKey = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id: string) => {
      const res = await SystemService.postApiSystemApiKeysByIdRevoke({
        path: { id },
        headers: getAuthHeaders(),
      });
      if (res.error) throw new Error((res.error as any)?.error ?? "Kunne ikke tilbakekalle nøkkel");
      return res.data;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ["system", "api-keys"] }),
  });
};
